// The nav tree, in one place. SideNav.vue renders it; header.vue uses it to resolve the
// current page title in sidebar mode. Gates are keys on the object returned by
// computeNavPermissions() — same role rules header.vue has always applied, lifted verbatim.
//
// Item shapes:
//   { label, to, icon, gate? }                         internal route
//   { label, href, icon, gate?, external: true }       external link (new tab)
//   { label, icon, gate?, group: true, children: [...] }
//   children may include { section: "Heading" } dividers (used by swiftComply)

function getRolesFromStorage(key) {
  let raw = null
  try {
    raw = window?.localStorage?.getItem(key)
  } catch (e) {}
  if (!raw) return []

  const toList = v =>
    String(v || "")
      .split(",")
      .map(x => String(x || "").trim())
      .filter(Boolean)

  try {
    const parsed = JSON.parse(raw)
    if (Array.isArray(parsed)) return parsed.map(x => String(x || "").trim()).filter(Boolean)
    return toList(parsed)
  } catch {
    return toList(raw)
  }
}

function rolesInclude(roles, role) {
  const target = String(role || "").toLowerCase().trim()
  return (roles || []).map(x => String(x || "").toLowerCase().trim()).includes(target)
}

export function computeNavPermissions() {
  const roles = getRolesFromStorage("active_user_roles")
  const isCorp = rolesInclude(roles, "Corp")
  return {
    isCorp,
    isPostClosing: rolesInclude(roles, "Post Closer") || isCorp,
    canViewRepricedLocks: rolesInclude(roles, "Compensation Admin"),
    canViewHmda: isCorp || rolesInclude(roles, "HMDA")
  }
}

export function applicationAdminLink() {
  return `${process.env.VUE_APP_LOANLAUNCH_APP_ENDPOINT}/applicationAdmin`
}

export const NAV_ITEMS = [
  { label: "Command Center", to: "/", icon: "fa-light fa-grid-2" },
  { label: "Applications", to: "/Applications", icon: "fa-light fa-users" },
  // Scenario Mode — no loanId. Opens in the same tab now (was: external tab) so it behaves
  // like every other internal nav item; the per-loan button in the Pipeline grid instead opens
  // as a left-side flyout so users don’t lose their place on the grid (see ProcessingV2.vue).
  { label: "Pricing Engine", to: "/polly-pricer", icon: "fa-light fa-calculator" },
  { label: "Pipeline", to: "/Processing", icon: "fa-light fa-table-cells-large" },
  { label: "Leaderboard", to: "/Leaderboard", icon: "fa-light fa-trophy" },
  // LoRedirect.vue — the LO picks and manages their own marketing site. No loanId, no
  // role gate: every LO manages their own. Route is /LORedirect; isPathActive lowercases
  // both sides, so the /loredirect URL matches too.
  { label: "My Website", to: "/LORedirect", icon: "fa-light fa-globe" },
  { label: "swiftPC", to: "/PostCloseConnect", icon: "fa-light fa-desktop", gate: "isPostClosing" },
  { label: "swiftUW", to: "/Insights", icon: "fa-light fa-building", gate: "isCorp" },
  {
    label: "swiftLocks",
    icon: "fa-light fa-lock",
    gate: "canViewRepricedLocks",
    group: true,
    children: [
      { label: "Work Queue", to: "/lock-desk-queue" },
      // Reporting on the revenue alerts the Work Queue holds — the alerts themselves are
      // worked from the queue, this reads them back.
      { label: "Revenue Analysis", to: "/revenue-analysis" },
      { label: "Lock Reprice", to: "/reprice" },
      { label: "Lock Extensions", to: "/extensions" },
      { label: "Extension Request", to: "/lock-extension-request" },
      { label: "Price Sheet", to: "/loan-price-sheet" },
      // Moved in here from the top level, where it was ungated. Children are not gate-
      // filtered by either renderer, so this now rides the group's canViewRepricedLocks
      // gate; the page still self-gates off the Polly roster on top of that.
      { label: "Concessions", to: "/concessions" }
    ]
  },
  {
    label: "swiftComply",
    icon: "fa-light fa-shield-check",
    gate: "canViewHmda",
    group: true,
    children: [
      { section: "Disclosures" },
      { label: "ICD", to: "/ICD" },
      { section: "Loan Status" },
      { label: "Loan Status", to: "/hmda-withdrawals" },
      { section: "Insuring" },
      { label: "Closing Packages", to: "/ai-insuring" },
      { label: "Upload FHA MIP", to: "/Insuring" },
      { label: "FHA MIP Payments", to: "/fha-mip-payments" },
      { section: "Post Consummation" },
      { label: "PCCD", to: "/pccd" }
    ]
  },
  {
    label: "Application Admin",
    href: applicationAdminLink,
    icon: "fa-light fa-user-gear",
    gate: "isCorp",
    external: true
  },
  { label: "Notification Management", to: "/NotificationManagement", icon: "fa-light fa-bell" }
]

// Routes are case-insensitive in vue-router; compare lowercase. "/" must be exact or it
// would light up on every page.
export function isPathActive(routePath, to) {
  const path = String(routePath || "").toLowerCase()
  const target = String(to || "").toLowerCase()
  if (target === "/") return path === "/"
  return path === target || path.startsWith(target + "/")
}

export function filterByPermissions(items, perms) {
  return items.filter(i => !i.gate || perms[i.gate])
}

// Page title for the top bar: deepest matching leaf, with its group's icon as fallback.
export function resolvePageTitle(routePath) {
  for (const item of NAV_ITEMS) {
    if (item.group) {
      const hit = item.children.find(c => c.to && isPathActive(routePath, c.to))
      if (hit) return { label: hit.label, parent: item.label, icon: item.icon }
    } else if (item.to && isPathActive(routePath, item.to)) {
      return { label: item.label, parent: null, icon: item.icon }
    }
  }
  return null
}